import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PlayerCard } from '../components/custom/PlayerCard';
import { playersAPI } from '../lib/api';
import { Search, Filter, Loader2, Users } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const positions = [
  { value: 'all', label: 'Todas las posiciones' },
  { value: 'GK', label: 'Portera' },
  { value: 'DEF', label: 'Defensa' },
  { value: 'MID', label: 'Mediocampista' },
  { value: 'FWD', label: 'Delantera' },
];

const availabilities = [
  { value: 'all', label: 'Cualquier disponibilidad' },
  { value: 'transfer', label: 'Transferencia' },
  { value: 'loan', label: 'Préstamo' },
  { value: 'both', label: 'Ambos' },
];

export const PlayersPage = () => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    position: 'all',
    availability: 'all',
    nationality: '',
  });

  useEffect(() => {
    loadPlayers();
  }, [filters]);

  const loadPlayers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.position !== 'all') params.position = filters.position;
      if (filters.availability !== 'all') params.availability = filters.availability;
      if (filters.nationality) params.nationality = filters.nationality;
      
      const response = await playersAPI.getAll(params);
      setPlayers(response.data);
    } catch (error) {
      console.error('Error loading players:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      position: 'all',
      availability: 'all',
      nationality: '',
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="players-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-800 uppercase tracking-tight">
              Mis Jugadoras
            </h1>
            <p className="text-slate-500 mt-1">
              Gestiona las jugadoras que has publicado en la red
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por nacionalidad..."
                  value={filters.nationality}
                  onChange={(e) => setFilters({ ...filters, nationality: e.target.value })}
                  className="pl-10"
                  data-testid="filter-nationality"
                />
              </div>
            </div>
            
            <Select
              value={filters.position}
              onValueChange={(value) => setFilters({ ...filters, position: value })}
            >
              <SelectTrigger className="w-full md:w-48" data-testid="filter-position">
                <SelectValue placeholder="Posición" />
              </SelectTrigger>
              <SelectContent>
                {positions.map((pos) => (
                  <SelectItem key={pos.value} value={pos.value}>
                    {pos.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.availability}
              onValueChange={(value) => setFilters({ ...filters, availability: value })}
            >
              <SelectTrigger className="w-full md:w-48" data-testid="filter-availability">
                <SelectValue placeholder="Disponibilidad" />
              </SelectTrigger>
              <SelectContent>
                {availabilities.map((av) => (
                  <SelectItem key={av.value} value={av.value}>
                    {av.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="outline" onClick={clearFilters} data-testid="clear-filters">
              <Filter className="w-4 h-4 mr-2" />
              Limpiar
            </Button>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : players.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {players.map((player) => (
              <PlayerCard key={player.id} player={player} />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold text-slate-600 uppercase">
              No hay jugadoras
            </h3>
            <p className="text-slate-500 mt-2">
              Aún no has publicado ninguna jugadora
            </p>
            <Button onClick={clearFilters} className="mt-4" variant="outline">
              Limpiar filtros
            </Button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PlayersPage;
